
import React from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Clock, DollarSign, Star, ArrowRight, CheckCircle } from 'lucide-react';
import { ConcernTag } from './JUVTriageQuiz';
import { treatmentEngine, Treatment, TreatmentBundle } from './TreatmentRecommendationEngine';

interface QuizResultsProps {
  concerns: ConcernTag[];
  budget: string;
  timeCommitment: string;
  resultsGoal: string;
  onRetakeQuiz: () => void;
  onBookTreatment: (treatmentId: string) => void;
  onBookBundle: (bundleId: string) => void;
}

const QuizResults = ({
  concerns,
  budget,
  timeCommitment,
  resultsGoal,
  onRetakeQuiz,
  onBookTreatment,
  onBookBundle
}: QuizResultsProps) => {
  const recommendations = treatmentEngine.getRecommendations(concerns, budget, timeCommitment);

  const getConcernTitle = (tag: ConcernTag) => {
    const titles = {
      BrightEyes: 'Bright Eyes & Wrinkles',
      NaturalLip: 'Natural Lips & Smile',
      YouthRefresh: 'Youth Refresh & Skin',
      HairGlow: 'Hair Restoration',
      Wellness: 'Wellness & Energy',
      FullMenu: 'Complete JUV Experience'
    };
    return titles[tag];
  };

  return (
    <div className="max-w-6xl mx-auto p-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center mb-8"
      >
        <div className="flex justify-center mb-4">
          <div className="w-16 h-16 bg-juv-terracotta rounded-full flex items-center justify-center">
            <CheckCircle className="w-8 h-8 text-white" />
          </div>
        </div>
        <h2 className="text-4xl font-bold text-juv-deep-chestnut mb-4">
          Your Personalized JUV Plan
        </h2>
        <p className="text-lg text-gray-600 max-w-2xl mx-auto">
          Based on your concerns for {concerns.map(getConcernTitle).join(', ')}, 
          here are our expert recommendations tailored to your {budget.replace('-', ' ')} budget.
        </p>
      </motion.div>

      {/* Summary Card */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="mb-8"
      >
        <Card className="bg-gradient-to-r from-juv-soft-blush to-juv-cream-white border-juv-rose-clay/30">
          <CardContent className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-center">
              <div>
                <h4 className="font-semibold text-juv-deep-chestnut mb-2">Your Concerns</h4>
                <div className="space-y-1">
                  {concerns.map(concern => (
                    <Badge key={concern} variant="outline" className="border-juv-terracotta text-juv-terracotta">
                      {getConcernTitle(concern)}
                    </Badge>
                  ))}
                </div>
              </div>
              <div>
                <h4 className="font-semibold text-juv-deep-chestnut mb-2">Budget</h4>
                <Badge className="bg-juv-terracotta text-white">
                  {budget.replace('-', ' ').replace('under', 'Under $').replace('open-bundle', 'Bundle Packages')}
                </Badge>
              </div>
              <div>
                <h4 className="font-semibold text-juv-deep-chestnut mb-2">Time</h4>
                <Badge variant="outline" className="border-juv-rose-clay text-juv-deep-chestnut">
                  {timeCommitment.replace('-', ' ')}
                </Badge>
              </div>
              <div>
                <h4 className="font-semibold text-juv-deep-chestnut mb-2">Goal</h4>
                <Badge variant="outline" className="border-juv-rose-clay text-juv-deep-chestnut">
                  {resultsGoal}
                </Badge>
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Bundle Recommendations */}
      {recommendations.bundles.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mb-8"
        >
          <h3 className="text-2xl font-bold text-juv-deep-chestnut mb-6">
            🌟 Recommended Packages (Best Value)
          </h3>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {recommendations.bundles.slice(0, 2).map((bundle, index) => (
              <Card key={bundle.id} className="border-juv-terracotta/50 shadow-lg hover:shadow-xl transition-shadow">
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="text-juv-deep-chestnut text-xl mb-2">
                        {bundle.name}
                      </CardTitle>
                      <p className="text-sm text-gray-600">{bundle.description}</p>
                    </div>
                    <Badge className="bg-green-100 text-green-800 border-green-300">
                      Save ${bundle.originalValue - bundle.price}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="text-right">
                        <div className="text-sm text-gray-500 line-through">
                          ${bundle.originalValue} value
                        </div>
                        <div className="text-2xl font-bold text-juv-terracotta">
                          ${bundle.price}
                        </div>
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <h5 className="font-medium text-juv-deep-chestnut">Includes:</h5>
                      {bundle.treatments.map(treatmentId => {
                        const treatment = treatmentEngine.getTreatmentById(treatmentId);
                        return treatment ? (
                          <div key={treatmentId} className="flex items-center text-sm text-gray-600">
                            <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                            {treatment.name}
                          </div>
                        ) : null;
                      })}
                    </div>

                    <Button 
                      className="w-full bg-juv-terracotta hover:bg-juv-deep-chestnut text-white"
                      onClick={() => onBookBundle(bundle.id)}
                    >
                      Book Package - ${bundle.price}
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </motion.div>
      )}

      {/* Individual Treatments */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="mb-8"
      >
        <h3 className="text-2xl font-bold text-juv-deep-chestnut mb-6">
          Individual Treatments
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {recommendations.treatments.slice(0, 6).map((treatment, index) => (
            <Card key={treatment.id} className="border-juv-rose-clay/30 shadow-lg hover:shadow-xl transition-shadow">
              <CardHeader>
                <div className="flex items-start justify-between mb-2">
                  <Badge variant="outline" className="border-juv-terracotta text-juv-terracotta">
                    {treatment.category}
                  </Badge>
                  <div className="text-right">
                    <div className="text-xs text-gray-500 line-through">
                      ${treatment.visitorPrice}
                    </div>
                    <div className="font-bold text-juv-terracotta">
                      ${treatment.memberPrice}
                    </div>
                  </div>
                </div>
                <CardTitle className="text-lg text-juv-deep-chestnut">
                  {treatment.name}
                </CardTitle>
                <p className="text-sm text-gray-600">{treatment.description}</p>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between text-sm text-gray-600">
                    <div className="flex items-center">
                      <Clock className="w-4 h-4 mr-1" />
                      {treatment.duration} min
                    </div>
                    <div className="text-green-600 font-medium">
                      Save ${treatment.visitorPrice - treatment.memberPrice}
                    </div>
                  </div>

                  <div className="space-y-1">
                    {treatment.benefits.slice(0, 2).map((benefit, idx) => (
                      <div key={idx} className="flex items-center text-sm text-gray-600">
                        <Star className="w-3 h-3 text-juv-terracotta mr-2" />
                        {benefit}
                      </div>
                    ))}
                  </div>

                  <Button 
                    className="w-full bg-juv-terracotta hover:bg-juv-deep-chestnut text-white"
                    onClick={() => onBookTreatment(treatment.id)}
                  >
                    Book Treatment
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </motion.div>

      {/* CTA Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="text-center"
      >
        <Card className="bg-gradient-to-r from-juv-terracotta to-juv-deep-chestnut text-white">
          <CardContent className="p-8">
            <h3 className="text-2xl font-bold mb-4">
              Ready to Start Your Glow Journey?
            </h3>
            <p className="text-lg opacity-90 mb-6">
              Book your consultation today and let our experts create your personalized treatment plan.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                variant="outline"
                className="border-white text-white hover:bg-white hover:text-juv-terracotta"
                onClick={() => console.log('Book consultation')}
              >
                Book Free Consultation
              </Button>
              <Button 
                variant="outline"
                className="border-white text-white hover:bg-white hover:text-juv-terracotta"
                onClick={onRetakeQuiz}
              >
                Retake Quiz
              </Button>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
};

export default QuizResults;
